// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

const CREDITS_QUERY_KEY = ['credits'] as const;

const EXPERIENCE_DETAILS_QUERY_KEY = (experienceId: string) =>
  ['experiences', experienceId, 'details'] as const;

/** Hook for fetching all detail lines for a work experience. */
export function useExperienceDetails(experienceId: string | undefined) {
  return useQuery({
    queryKey: EXPERIENCE_DETAILS_QUERY_KEY(experienceId ?? ''),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/api/v1/experiences/{id}/details',
        { params: { path: { id: experienceId! } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    enabled: !!experienceId,
  });
}

/** Hook for creating a single detail line for a work experience. */
export function useCreateExperienceDetail(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/{id}/details',
        {
          params: { path: { id: experienceId } },
          body: { content },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EXPERIENCE_DETAILS_QUERY_KEY(experienceId),
      });
    },
  });
}

/** Hook for bulk creating detail lines for a work experience. */
export function useBulkCreateExperienceDetails(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contents: string[]) => {
      const items = contents.map((content) => ({ content }));
      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/{id}/details/bulk',
        {
          params: { path: { id: experienceId } },
          body: { items },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EXPERIENCE_DETAILS_QUERY_KEY(experienceId),
      });
    },
  });
}

/** Hook for updating a detail line. */
export function useUpdateExperienceDetail(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      detailId,
      content,
    }: {
      detailId: string;
      content: string;
    }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/experiences/{id}/details/{detailId}',
        {
          params: { path: { id: experienceId, detailId } },
          body: { content },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EXPERIENCE_DETAILS_QUERY_KEY(experienceId),
      });
    },
  });
}

/** Hook for deleting a detail line. */
export function useDeleteExperienceDetail(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (detailId: string) => {
      const { data, error } = await apiClient.DELETE(
        '/api/v1/experiences/{id}/details/{detailId}',
        { params: { path: { id: experienceId, detailId } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EXPERIENCE_DETAILS_QUERY_KEY(experienceId),
      });
    },
  });
}

/** Hook for reordering detail lines. */
export function useReorderExperienceDetails(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (detailIds: string[]) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/experiences/{id}/details/reorder',
        {
          params: { path: { id: experienceId } },
          body: { detail_ids: detailIds },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        EXPERIENCE_DETAILS_QUERY_KEY(experienceId),
        data,
      );
    },
  });
}

/** Hook for AI-suggesting detail lines. */
export function useSuggestExperienceDetails(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: {
      job_title?: string;
      company_name?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/{id}/details/suggest',
        {
          params: { path: { id: experienceId } },
          body: params ?? {},
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

/** Hook for AI-polishing a single detail line into a CV-ready bullet. */
export function usePolishExperienceDetail(experienceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      detailId,
      jobTitle,
    }: {
      detailId: string;
      jobTitle?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/{id}/details/{detailId}/polish',
        {
          params: { path: { id: experienceId, detailId } },
          body: jobTitle ? { job_title: jobTitle } : {},
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}


